// src/layouts/AppLayout.jsx

import { Outlet, useLocation } from 'react-router-dom';
import { Suspense, useState, useEffect } from 'react';
import { useAuth } from '../auth/authContext';
import { useTranslation } from 'react-i18next';

import AppSidebar from './components/AppSidebar';
import AppHeader from './components/AppHeader';
import AppMain from './components/AppMain';
import ScrollToTopApp from '../utils/ScrollToTopApp';

import OnboardingModal from '../auth/user/onboarding/OnboardingModal.jsx';
import HelpSupportModal from '../components/support/HelpSupportModal.jsx';
import useActiveUserPresenceHeartbeat
  from '../hooks/useActiveUserPresenceHeartbeat';

const DESKTOP_SIDEBAR_MEDIA_QUERY = '(min-width: 960px)';

function isDesktopSidebarViewport() {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY).matches;
}

export default function AppLayout() {
  const { user, loadingUser } = useAuth();

  const [effectiveRole, setEffectiveRole] = useState(user?.role || 'guest');
  const [isOnboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [isHelpSupportOpen, setHelpSupportOpen] = useState(false);

  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    isDesktopSidebarViewport
  );
  const location = useLocation();

  useActiveUserPresenceHeartbeat({
    enabled: !loadingUser && Boolean(user),
    currentRoute: location.pathname,
  });

  const [hasShownOnboarding, setHasShownOnboarding] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY);
    const handleViewportChange = (event) => {
      setIsDesktopViewport(event.matches);

      if (event.matches) {
        setSidebarOpen(false);
      }
    };

    setIsDesktopViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (isDesktopViewport || !sidebarOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDesktopViewport, sidebarOpen]);

  useEffect(() => {
    if (loadingUser || !user) {
      return;
    }

    if (user.onboardingCompleted === true) {
      setOnboardingModalOpen(false);
      return;
    }

    if (!hasShownOnboarding && user.onboardingCompleted === false) {
      setOnboardingModalOpen(true);
      setHasShownOnboarding(true);
    }
  }, [user, loadingUser, hasShownOnboarding]);

  useEffect(() => {
    const handleOpenOnboarding = () => {
      setOnboardingModalOpen(true);
    };

    window.addEventListener('openOnboarding', handleOpenOnboarding);

    return () => {
      window.removeEventListener('openOnboarding', handleOpenOnboarding);
    };
  }, []);

  return (
    <div
      className={`app-layout ${
        sidebarOpen ? 'sidebar-open' : 'sidebar-closed'
      }`}
    >
      {/* Sidebar Overlay */}
      {!isDesktopViewport && sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <AppSidebar
        t={t}
        sidebarOpen={sidebarOpen}
        isDesktopViewport={isDesktopViewport}
        setSidebarOpen={setSidebarOpen}
        effectiveRole={effectiveRole}
        setEffectiveRole={setEffectiveRole}
        onOpenHelpSupport={() => setHelpSupportOpen(true)}
      />

      <div className="app-main">
        <AppHeader
          user={user}
          sidebarOpen={sidebarOpen}
          isDesktopViewport={isDesktopViewport}
          onToggleSidebar={() => {
            setSidebarOpen((currentValue) => !currentValue);
          }}
          effectiveRole={effectiveRole}
        />

        {/* 🔥 scroll per canvi de ruta */}
        <ScrollToTopApp dependency={location.pathname} />

        <AppMain>
          <Suspense fallback={<p>{t('loading')}</p>}>
            <Outlet />
          </Suspense>
        </AppMain>
      </div>

      {/* 🧠 ONBOARDING */}
      <OnboardingModal
        isOpen={isOnboardingModalOpen}
        onRequestClose={() => setOnboardingModalOpen(false)}
      />

      <HelpSupportModal
        isOpen={isHelpSupportOpen}
        onRequestClose={() => setHelpSupportOpen(false)}
      />
    </div>
  );
}
